import React, {useEffect} from 'react';
import {useLocation} from 'react-router-dom';
import {HelmetProvider} from 'react-helmet-async';
import ReactGA from 'react-ga4';

// Importations CSS importantes
import i18n from 'packages/common/locales/i18n';
import {isLanguageSupported} from 'packages/common/locales/languageConfig';
import 'simplebar-react/dist/simplebar.min.css';
import 'react-quill/dist/quill.snow.css';
import 'react-lazy-load-image-component/src/effects/blur.css';

// Providers et composants importants
import {LocalizationProvider} from 'packages/common/locales';
import ThemeProvider from 'packages/common/theme';
import {SettingsProvider} from 'packages/common/components/settings';
import {MotionLazy} from 'packages/common/components/animate/motion-lazy';
import {SnackbarProvider} from 'packages/common/components/snackbar';
import {NetworkStatusBanner} from 'packages/common/components/network-status-banner';
import Router from './routes';
import {LoadingScreen} from 'packages/common/components/loading-screen';
import {Suspense} from 'react';
import ProgressBar from 'packages/common/components/progress-bar';

export default function App() {
  const location = useLocation();

  // Appliquer la langue transmise par le widget via le paramètre ?lang=
  // S'exécute une seule fois au montage initial pour éviter les flashs pendant la navigation
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const lang = params.get('lang');
    if (lang && isLanguageSupported(lang) && lang !== i18n.language) {
      i18n.changeLanguage(lang);
    }
    // Dependencies vides volontairement : on ne change la langue qu'au montage initial de l'app
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // GA INIT - Uniquement en production
  useEffect(() => {
    try {
      if (process.env.NODE_ENV === 'production' && process.env.REACT_APP_GA_MEASUREMENT_ID) {
        ReactGA.initialize(process.env.REACT_APP_GA_MEASUREMENT_ID);
      }
    } catch (error) {
      console.error('Failed to initialize GA4:', error);
    }
  }, []);

  // GA PAGEVIEW - Uniquement en production
  useEffect(() => {
    if (process.env.NODE_ENV === 'production') {
      ReactGA.send({
        hitType: 'pageview',
        page: location.pathname + location.search,
      });
    }
  }, [location.pathname, location.search]);

  return (
    <HelmetProvider>
      <LocalizationProvider>
        <SettingsProvider
          defaultSettings={{
            themeMode: 'light', // 'light' | 'dark'
            themeDirection: 'ltr', //  'rtl' | 'ltr'
            themeContrast: 'default', // 'default' | 'bold'
            themeLayout: 'vertical', // 'vertical' | 'horizontal' | 'mini'
            themeColorPresets: 'default', // 'default' | 'cyan' | 'purple' | 'blue' | 'orange' | 'red'
            themeStretch: false,
          }}
        >
          <ThemeProvider>
            <MotionLazy>
              <SnackbarProvider>
                <NetworkStatusBanner />
                <ProgressBar />
                <Suspense fallback={<LoadingScreen />}>
                  <Router />
                </Suspense>
              </SnackbarProvider>
            </MotionLazy>
          </ThemeProvider>
        </SettingsProvider>
      </LocalizationProvider>
    </HelmetProvider>
  );
}
